import { useEffect, useState } from "react";
import { Save, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { api, asError } from "@/api/client";

export default function AdminSettings() {
  const [form, setForm] = useState({
    free_shipping_threshold: "",
    shipping_fee: "",
    banner_text: "",
    banner_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => {
        setForm({
          free_shipping_threshold: data.free_shipping_threshold ?? 500,
          shipping_fee: data.shipping_fee ?? 49,
          banner_text: data.banner_text ?? "",
          banner_active: data.banner_active ?? true,
        });
      })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        free_shipping_threshold: Number(form.free_shipping_threshold),
        shipping_fee: Number(form.shipping_fee),
        banner_text: form.banner_text,
        banner_active: form.banner_active,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(asError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[var(--ink-3)] kbd text-xs">Loading…</div>;

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <form onSubmit={save} className="bg-white border border-[#e5dec9] rounded-xl p-6 md:p-8 space-y-5">
        <div>
          <div className="kbd text-[0.6rem] text-[var(--gold)] flex items-center gap-1.5"><SettingsIcon size={11} /> Store Settings</div>
          <h2 className="font-serif-display text-2xl text-[var(--ink)] mt-2">Shipping & Storefront</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Free Shipping Above ₹" type="number" value={form.free_shipping_threshold} onChange={(v) => setForm({ ...form, free_shipping_threshold: v })} testId="settings-free-threshold" />
          <Field label="Shipping Fee ₹" type="number" value={form.shipping_fee} onChange={(v) => setForm({ ...form, shipping_fee: v })} testId="settings-shipping-fee" />
        </div>

        <div>
          <label className="kbd text-[0.6rem] text-[var(--ink-3)] mb-1.5 block">Storefront Banner Text</label>
          <input
            data-testid="settings-banner-text"
            value={form.banner_text}
            onChange={(e) => setForm({ ...form, banner_text: e.target.value })}
            placeholder="Free shipping on orders above ₹500…"
            className="w-full bg-white border border-[#e5dec9] rounded-md px-3 py-2.5 text-sm focus:border-[var(--green)] outline-none"
          />
        </div>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.banner_active} onChange={(e) => setForm({ ...form, banner_active: e.target.checked })} />
          Show banner on storefront
        </label>

        <div className="pt-4 flex justify-end">
          <button data-testid="settings-save" disabled={saving} type="submit" className="btn-primary disabled:opacity-60">
            <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="kbd text-[0.6rem] text-[var(--ink-3)] mb-1.5 block">{label}</label>
      <input
        type={type}
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#e5dec9] rounded-md px-3 py-2.5 text-sm focus:border-[var(--green)] outline-none"
      />
    </div>
  );
}
